/* Aftershock collection card image slider (Impact 7.2).
 *
 * Progressive enhancement over the theme's own <product-card>. It never edits
 * the card's markup in place: it adds an absolutely positioned scroll-snap
 * track over the existing image plus a click-through controls layer, so the
 * card height cannot change (no layout shift) and the image link and the Swish
 * heart underneath are untouched.
 *
 * Config comes from snippets/card-image-slider.liquid:
 *   window.AftershockCardSlider = { maxImages, scope, page }
 *
 * Image source: /products/<handle>.js (public Shopify endpoint, one small JSON
 * per card, fetched only when the card is near the viewport, 4 at a time).
 *
 * Lazy loading: slide 1 is the image the theme already loads for its rollover.
 * Slides 3 and up get no src at all until the shopper first hovers, touches or
 * focuses that card.
 */
(function () {
  'use strict';

  if (window.__aftershockCardSlider) return;
  window.__aftershockCardSlider = true;

  var cfg = window.AftershockCardSlider || {};
  var maxImages = Math.min(6, Math.max(2, parseInt(cfg.maxImages, 10) || 4));
  var allowedPages = cfg.scope === 'all' ? ['collection', 'search', 'index'] : ['collection'];
  if (allowedPages.indexOf(cfg.page) === -1) return;

  // Respect Data Saver: no extra requests at all.
  var conn = navigator.connection;
  if (conn && conn.saveData) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Cards in these places are never enhanced. scroll-carousel is the important
  // one: homepage featured collections are horizontal carousels, and a
  // swipeable slider nested inside a swipeable carousel fights for the gesture.
  var EXCLUDE =
    'scroll-carousel, .navigation-promo, predictive-search, quick-buy-drawer, cart-drawer, [data-no-card-slider]';

  var CHEVRON =
    '<svg aria-hidden="true" focusable="false" width="8" height="12" viewBox="0 0 8 12" fill="none">' +
    '<path d="m1.5 1 5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var instances = new Set();

  /* ------------------------------------------------------------------ data */

  var cache = new Map();
  var waiting = [];
  var active = 0;

  function pump() {
    while (active < 4 && waiting.length) {
      active++;
      waiting.shift()();
    }
  }

  function root() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  function getImages(handle) {
    if (cache.has(handle)) return cache.get(handle);
    var key = 'card-slider:' + handle;
    try {
      var stored = sessionStorage.getItem(key);
      if (stored) {
        var parsed = Promise.resolve(JSON.parse(stored));
        cache.set(handle, parsed);
        return parsed;
      }
    } catch (e) {}
    var promise = new Promise(function (resolve) {
      waiting.push(function () {
        fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (json) {
            var images = json && Array.isArray(json.images) ? json.images : null;
            if (images) {
              try {
                sessionStorage.setItem(key, JSON.stringify(images));
              } catch (e) {}
            }
            resolve(images);
          })
          .catch(function () {
            resolve(null);
          })
          .then(function () {
            active--;
            pump();
          });
      });
      pump();
    });
    cache.set(handle, promise);
    return promise;
  }

  /* --------------------------------------------------------------- helpers */

  // Compare images by file name so /files/X.jpg and /products/X.jpg?v=1&width=533 match.
  function fileKey(url) {
    try {
      return new URL(url, location.href).pathname
        .split('/')
        .pop()
        .replace(/_\d+x\d*(?=\.)/, '')
        .toLowerCase();
    } catch (e) {
      return String(url);
    }
  }

  function absolute(url) {
    return url.indexOf('//') === 0 ? 'https:' + url : url;
  }

  function withWidth(url, w) {
    var u = absolute(url);
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  var WIDTHS = [300, 400, 500, 600, 800];

  function makeImg(url, like) {
    var img = document.createElement('img');
    img.className = 'card-slider__img';
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    img.sizes = like.getAttribute('sizes') || '(max-width: 699px) 50vw, 25vw';
    img.width = like.getAttribute('width') || 533;
    img.height = like.getAttribute('height') || 800;
    img.dataset.src = withWidth(url, 600);
    img.dataset.srcset = WIDTHS.map(function (w) {
      return withWidth(url, w) + ' ' + w + 'w';
    }).join(', ');
    return img;
  }

  function loadImg(img) {
    if (!img.dataset.src) return;
    img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    delete img.dataset.src;
    delete img.dataset.srcset;
  }

  function slide(img) {
    var s = document.createElement('div');
    s.className = 'card-slider__slide';
    s.appendChild(img);
    return s;
  }

  /* ----------------------------------------------------------------- mount */

  function mount(card, images) {
    var figure = card.querySelector('.product-card__figure');
    var link = figure && figure.querySelector(':scope > a[href*="/products/"]');
    var primary = link && link.querySelector('.product-card__image--primary');
    if (!primary || card.classList.contains('has-card-slider')) return false;

    // Slide 0 is a same-URL copy of the primary (already loaded, browser cache).
    var primaryKey = fileKey(primary.currentSrc || primary.src);
    var extra = [];
    var seen = {};
    seen[primaryKey] = true;
    images.forEach(function (url) {
      var k = fileKey(url);
      if (!seen[k]) {
        seen[k] = true;
        extra.push(url);
      }
    });
    extra = extra.slice(0, maxImages - 1);
    if (!extra.length) return false; // single-image product: leave the theme rollover alone

    var img0 = primary.cloneNode(false);
    img0.className = 'card-slider__img';
    img0.removeAttribute('fetchpriority');
    img0.removeAttribute('loading');
    img0.draggable = false;

    var slides = [slide(img0)];
    var pending = [];

    // Slide 1: reuse the theme's own rollover image when it is the same file,
    // so no second request is made for it.
    var secondary = link.querySelector('.product-card__image--secondary');
    extra.forEach(function (url, i) {
      var img;
      if (i === 0 && secondary && fileKey(secondary.getAttribute('src') || '') === fileKey(url)) {
        img = secondary.cloneNode(false);
        img.className = 'card-slider__img';
        img.alt = '';
        img.removeAttribute('aria-hidden');
        img.removeAttribute('fetchpriority');
        img.draggable = false;
      } else {
        img = makeImg(url, primary);
        if (i === 0) loadImg(img);
        else pending.push(img);
      }
      slides.push(slide(img));
    });

    var count = slides.length;
    var track = document.createElement('div');
    track.className = 'card-slider__track';
    track.tabIndex = -1; // stop Chrome making the scroller a tab stop
    slides.forEach(function (s) {
      track.appendChild(s);
    });

    var ui = document.createElement('div');
    ui.className = 'card-slider__ui';
    ui.setAttribute('role', 'group');
    ui.setAttribute('aria-label', 'Product images');

    function arrow(dir, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'card-slider__arrow card-slider__arrow--' + dir;
      b.setAttribute('aria-label', label);
      b.innerHTML = CHEVRON;
      return b;
    }
    var prev = arrow('prev', 'Previous image');
    var next = arrow('next', 'Next image');

    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'card-slider__dots';
    dotsWrap.setAttribute('aria-hidden', 'true');
    var dots = slides.map(function () {
      var d = document.createElement('span');
      d.className = 'card-slider__dot';
      dotsWrap.appendChild(d);
      return d;
    });

    var status = document.createElement('span');
    status.className = 'sr-only';
    status.setAttribute('aria-live', 'polite');

    ui.appendChild(prev);
    ui.appendChild(next);
    ui.appendChild(dotsWrap);
    ui.appendChild(status);

    var state = { card: card, track: track, ui: ui, current: 0, slideLeft: null };

    function render(announce) {
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === state.current);
      });
      prev.setAttribute('aria-disabled', String(state.current === 0));
      next.setAttribute('aria-disabled', String(state.current === count - 1));
      if (announce) status.textContent = 'Image ' + (state.current + 1) + ' of ' + count;
    }

    // Exact (fractional) scroll position of slide i. clientWidth is rounded to a
    // whole pixel, so on fractional card widths it would land a sliver short.
    function slideLeft(i) {
      return slides[i].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    }

    function go(i) {
      i = Math.max(0, Math.min(count - 1, i));
      wake();
      track.scrollTo({ left: slideLeft(i), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    var woken = false;
    function wake() {
      if (woken) return;
      woken = true;
      pending.forEach(loadImg);
    }

    var raf = 0;
    track.addEventListener(
      'scroll',
      function () {
        wake();
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          var w = track.getBoundingClientRect().width || 1;
          var i = Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / w)));
          if (i !== state.current) {
            state.current = i;
            render(true);
          }
        });
      },
      { passive: true }
    );

    prev.addEventListener('click', function () {
      if (prev.getAttribute('aria-disabled') !== 'true') go(state.current - 1);
    });
    next.addEventListener('click', function () {
      if (next.getAttribute('aria-disabled') !== 'true') go(state.current + 1);
    });

    // First sign of interest in this card: load the remaining slides.
    ['pointerenter', 'touchstart', 'focusin'].forEach(function (type) {
      figure.addEventListener(type, wake, { once: true, passive: true });
    });

    // If the shopper picks a colour swatch the theme swaps the card's images.
    // Slides would then be stale, so hand the card back to the theme.
    // Also covers the Swatch King app (sa-swatch-* / swatch-king-*), which is
    // installed on this store but switched off for product grids today.
    var SWATCH = '.product-card__swatch-list, .product-card__variant-list, [sa-swatch-root], [class*="swatch"]';
    ['change', 'click'].forEach(function (type) {
      card.addEventListener(type, function (e) {
        if (instances.has(state) && e.target.closest && e.target.closest(SWATCH)) teardown(state);
      });
    });

    state.slideLeft = slideLeft;
    render(false);
    instances.add(state);

    // Swap in only once slide 0 is decoded, so there is no blank frame.
    var ready = img0.decode ? img0.decode().catch(function () {}) : Promise.resolve();
    Promise.race([
      ready,
      new Promise(function (r) {
        setTimeout(r, 800);
      }),
    ]).then(function () {
      if (!card.isConnected || !instances.has(state)) return;
      link.insertBefore(track, link.firstChild);
      figure.appendChild(ui);
      card.classList.add('has-card-slider');
    });
    return true;
  }

  function teardown(state) {
    instances.delete(state);
    state.track.remove();
    state.ui.remove();
    state.card.classList.remove('has-card-slider');
    state.card.dataset.cardSlider = 'off';
  }

  /* -------------------------------------------------------------- discovery */

  function start(card) {
    var handle = card.getAttribute('handle');
    if (!handle) {
      card.dataset.cardSlider = 'off';
      return;
    }
    getImages(handle).then(function (images) {
      if (!images || images.length < 2 || !card.isConnected) {
        card.dataset.cardSlider = 'off';
        return;
      }
      card.dataset.cardSlider = mount(card, images) ? 'on' : 'off';
    });
  }

  var io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                io.unobserve(entry.target);
                start(entry.target);
              }
            });
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  function scan() {
    document.querySelectorAll('product-card:not([data-card-slider])').forEach(function (card) {
      if (card.closest(EXCLUDE) || !card.querySelector('.product-card__figure')) {
        card.dataset.cardSlider = 'off';
        return;
      }
      card.dataset.cardSlider = 'pending';
      if (io) io.observe(card);
      else start(card);
    });
  }

  // Cards added later (filtering, pagination, section reloads in the editor).
  var scanTimer = 0;
  function scheduleScan() {
    if (scanTimer) return;
    scanTimer = setTimeout(function () {
      scanTimer = 0;
      scan();
    }, 50);
  }

  function init() {
    scan();
    new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Keep the visible slide aligned when the viewport width changes.
  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      instances.forEach(function (s) {
        if (!s.card.isConnected) {
          instances.delete(s);
          return;
        }
        s.track.scrollLeft = s.slideLeft(s.current);
      });
    }, 150);
  });
})();